import { useTransition, useState } from "react";

import { useSearchParams } from "react-router";
import { type OperationType } from "relay-runtime";
import { useDebouncedCallback } from "use-debounce";
import { useDeepCompareCallback, useDeepCompareMemo } from "use-deep-compare";

import { SEARCH_DEBOUNCE_MS, DEFAULT_PAGE_SIZE } from "app/constants";

import { sortModelToOrder } from "./sortModelToOrder";

import type { GridSortModel } from "@mui/x-data-grid-pro";
import type { RefetchFnDynamic } from "react-relay";

type QueryArgs = Record<string, unknown>;

// Type for fragment keys used by React Relay
// Unfortuantely not exported from the package
type KeyType =
  | Readonly<{
      " $data"?: unknown;
      " $fragmentSpreads": unknown;
    }>
  | null
  | undefined;

/**
 * Converts backend order string to GridSortModel
 */
function parseOrderToSortModel(
  order: string,
  sortKeys: Map<string, string>,
): GridSortModel | null {
  if (!order) {
    return null;
  }

  const isDesc = order.endsWith("-");
  const sortKey = isDesc ? order.slice(0, -1) : order;

  // Find the frontend field name from the backend sort key
  let field = sortKey;
  for (const [frontendField, backendKey] of sortKeys.entries()) {
    if (backendKey === sortKey) {
      field = frontendField;
      break;
    }
  }

  return [{ field, sort: isDesc ? "desc" : "asc" }];
}

export function useSortableGrid<
  TQuery extends OperationType,
  TKey extends KeyType,
>(
  refetch: RefetchFnDynamic<TQuery, TKey>,
  sortKeys = new Map<string, string>(),
): [
  (sortModel: GridSortModel | null, args?: QueryArgs) => void,
  boolean,
  GridSortModel | null,
] {
  const [isPending, startTransition] = useTransition();
  const [searchParams, setSearchParams] = useSearchParams({});

  // Only the leading sort is sent: `parseOrderToSortModel` reads a single
  // field back out of the URL, so a multi-column order couldn't round-trip.
  const toOrder = (sortModel: GridSortModel | null) =>
    sortModelToOrder(sortModel?.slice(0, 1), sortKeys);

  // We need to manage sort model state locally to ensure immediate updates
  const [localSortModel, setLocalSortModel] = useState<GridSortModel | null>(
    null,
  );

  // Get sort model from URL
  const sortParam = searchParams.get("sort");

  // Get sort model from URL and use local state for immediate updates
  const sortModel: GridSortModel | null = useDeepCompareMemo(() => {
    const urlSortModel = sortParam
      ? parseOrderToSortModel(sortParam, sortKeys)
      : null;

    return localSortModel ?? urlSortModel;
  }, [sortParam, sortKeys, localSortModel]);

  // Function to update sort in URL
  const setSortParams = useDeepCompareCallback(
    (sortModel: GridSortModel | null) => {
      const newParams = new URLSearchParams(searchParams);
      const order = toOrder(sortModel);

      if (!order && newParams.has("sort")) {
        newParams.delete("sort");
        setSearchParams(newParams, { replace: true });
      }

      if (order && newParams.get("sort") !== order) {
        newParams.set("sort", order);
        setSearchParams(newParams, { replace: true });
      }
    },
    [searchParams, setSearchParams, sortKeys],
  );

  // Debounced function that updates both URL params and refetches with 200ms delay
  const debouncedRefetch = useDebouncedCallback(
    (sortModel: GridSortModel | null, args: QueryArgs = {}) => {
      // Update URL params and refetch together
      setSortParams(sortModel);

      const order = toOrder(sortModel);

      startTransition(() => {
        refetch({
          first: DEFAULT_PAGE_SIZE,
          order,
          ...args,
        });
      });
    },
    SEARCH_DEBOUNCE_MS,
  );

  const refetchModel = useDeepCompareCallback(
    (sortModel: GridSortModel | null, args: QueryArgs = {}) => {
      // Update local state immediately for better UX
      setLocalSortModel(sortModel);

      // Use debounced function that updates URL params and refetches together
      debouncedRefetch(sortModel, args);
    },
    [debouncedRefetch],
  );

  return [refetchModel, isPending, sortModel];
}
